import { Button, Col, Divider, Form, Input, Row, Select, Space } from "antd";
import { MaskedInput } from "antd-mask-input";
import { useAppDispatch, useAppSelector } from "../../../../../util/hooks";
import { PessoaModel } from "../../../../../models/pessoa/pessoaModel";
import { cadastrarPessoa } from "../../../../../services/pessoaService";
import { setModalPessoaContaFinanceira } from "../../../../../redux/slices/contaFinanceira/contaFinanceiraSlice";
import { buscarCep } from "../../../../../services/localizacaoService";
import { useEffect, useState } from "react";
import { mascaraCep } from "../../../../../util/mascaras";
import { CheckOutlined, LeftOutlined } from "@ant-design/icons";
import { EstadoModel } from "../../../../../models/localizacao/estadoModel";

type ModalPessoaContaFinanceiraProps = {
  origem: string;
};

const ModalPessoaContaFinanceira = ({ origem }: ModalPessoaContaFinanceiraProps) => {
  //estados globais
  const localizacaoState = useAppSelector((state) => state.localizacao);
  const pessoaState = useAppSelector((state) => state.pessoa);
  const persistedState = useAppSelector((state) => state.persisted);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();
  const [inputCep, setInputCep] = useState<string>("");
  const [tipo, setTipo] = useState<boolean>(false);

  //apenas o perfil ADMIN pode cadastrar pessoa sem CPF/CNPJ (empresas do exterior)
  const cpfCnpjOpcional = persistedState.user.perfil === "ADMIN";

  useEffect(() => {
    form.resetFields();
    form.setFieldValue("idCategoria", origem === "D" ? 1 : 2);
    setInputCep("");
  }, []);

  const submit = (value: any) => {
    const pessoaSubmit: PessoaModel = { ...value };
    pessoaSubmit.cep = value.cep?.replace(/[^0-9]/g, "");
    pessoaSubmit.cpfcnpj = value.cpfcnpj ? value.cpfcnpj.replace(/[^0-9]/g, "") : null;
    pessoaSubmit.categoriaPessoa = [{ idCategoriaPessoa: origem === "D" ? 1 : 2 }];
    pessoaSubmit.ativo = true;
    dispatch(cadastrarPessoa({ value: pessoaSubmit, busca: pessoaState.busca, origem: "contaFinanceira" }));
  };

  const onBuscarCep = async () => {
    const retorno = await dispatch(buscarCep(inputCep));
    form.setFieldsValue({
      endereco: retorno.payload.logradouro,
      bairro: retorno.payload.bairro,
      cidade: retorno.payload.localidade,
      idEstado: localizacaoState.listEstado.filter((a: EstadoModel) => a.sigla === retorno.payload.uf)[0].id,
    });
  };

  const onChangeTipo = (value: any) => {
    value === "PF" ? setTipo(false) : setTipo(true);
    form.setFieldValue("cpfcnpj", "");
  };

  const voltar = () => {
    dispatch(setModalPessoaContaFinanceira(false));
  };

  return (
    <Form form={form} layout="vertical" onFinish={submit}>
      <Row gutter={12}>
        <Col span={7}>
          <Form.Item
            label="Razão Social"
            name="razaoSocial"
            rules={[
              {
                required: true,
                message: "Digite a razão social",
                validateTrigger: "onSubmit",
              },
            ]}
          >
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={7}>
          <Form.Item
            label="Nome Fantasia"
            name="apelido"
            rules={[
              {
                required: true,
                message: "Digito o nome fantasia da empresa",
                validateTrigger: "onSubmit",
              },
            ]}
          >
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item
            label="Tipo Pessoa"
            name="tipoPessoa"
            rules={[
              {
                required: true,
                message: "Selecione o tipo pessoa",
              },
            ]}
          >
            <Select
              autoFocus
              optionFilterProp="label"
              onChange={onChangeTipo}
              options={[
                {
                  value: "PJ",
                  label: "PJ",
                },
                {
                  value: "PF",
                  label: "PF",
                },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="CPF / CNPJ"
            name="cpfcnpj"
            tooltip={cpfCnpjOpcional ? "Deixe em branco para empresas do exterior, que não possuem CPF/CNPJ" : undefined}
            rules={[
              {
                required: !cpfCnpjOpcional,
                message: "Digite o CPF ou CNPJ da pessoa",
                validateTrigger: "onSubmit",
              },
            ]}
          >
            {tipo ? <MaskedInput mask="00.000.000/0000-00"></MaskedInput> : <MaskedInput mask="000.000.000-00"></MaskedInput>}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={5}>
          <Form.Item label="Identidade" name="identidade">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label="Orgão emissor" name="orgaoEmissor">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={7}>
          <Form.Item label="Inscrição Estadual" name="inscricaoEstadual">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={7}>
          <Form.Item label="Inscrição Municipal" name="inscricaoMunicipal">
            <Input type="text" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item label="Site" name="homepage">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="E-mail" name="email">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Categoria"
            name="idCategoria"
            rules={[
              {
                required: true,
                message: "Selecione pelo menos uma categoria",
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Selecione a(s) categoria(s)"
              options={[
                { value: 1, label: "Fornecedor" },
                { value: 2, label: "Cliente" },
              ]}
              disabled
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left" orientationMargin="0">
        Endereço
      </Divider>
      <Row gutter={12}>
        <Col span={4}>
          <Form.Item label="CEP" name="cep">
            <MaskedInput style={{ width: "100%" }} mask={mascaraCep()} onChange={(e) => setInputCep(e.target.value)} />
          </Form.Item>
        </Col>
        <Col span={2}>
          <Form.Item label=" ">
            <Button style={{ marginLeft: -23, backgroundColor: "#f1f1f1" }} onClick={onBuscarCep}>
              Buscar
            </Button>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Endereço" name="endereco">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item label="Número" name="numero">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Complemento" name="complemento">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item label="UF" name="idEstado">
            <Select
              autoFocus
              showSearch
              optionFilterProp="label"
              loading={localizacaoState.loadingEstado}
              options={localizacaoState.listEstado.map((a: any) => ({
                value: a.id,
                label: a.sigla,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={6}>
          <Form.Item label="Bairro" name="bairro">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Cidade" name="cidade">
            <Input type="text" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Observação" name="observacao">
            <Input type="text" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={24} className="modalFooter">
          <Space size="middle">
            <Button className="cancelButton" onClick={voltar} icon={<LeftOutlined />}>
              VOLTAR
            </Button>
            <Button htmlType="submit" icon={<CheckOutlined />} className="buttonPrimary">
              SALVAR
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

export default ModalPessoaContaFinanceira;
